import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { ClipViewV2, User } from '../models/user';
import { ApiHelperService } from './api-helper.service';
import { ClipviewsService } from './clipviews.service';
import { LocalStorageService } from './local-storage.service';

interface QueuedClipView {
  event: ClipViewV2;
  queuedAt: string;
}

@Injectable({providedIn: 'root'})
export class ClipViewQueueService {
  private readonly maxQueueSize = 2000;
  private readonly maxBatchSize = 100;
  private readonly maxQueueAgeMs = 7 * 24 * 60 * 60 * 1000;
  private readonly maxRetryMs = 15 * 60 * 1000;
  private readonly batchFlushIntervalMs = 30000;
  private flushing = new Set<string>();
  private batchFlushTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private retryAttempts = new Map<string, number>();
  private retryTimers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(
    private apiHelper: ApiHelperService,
    private clipviewsService: ClipviewsService,
    private localStorageService: LocalStorageService,
  ) {
    window.addEventListener('online', () => this.flushCurrentUser());
    this.localStorageService.listenForUserUpdate().subscribe(user => {
      if (user?._id) this.flushCurrentUser();
    });
  }

  createEventId(): string {
    const cryptoApi: Crypto = window.crypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const value = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
  }

  enqueue(event: ClipViewV2, flushImmediately: boolean = false) {
    if (!event?.userid || !event.teamid || !event.eventid) return;
    const queue = this.readQueue(event.userid, event.teamid);
    if (_.some(queue, item => item.event.eventid === event.eventid)) return;
    queue.push({event, queuedAt: new Date().toISOString()});
    if (queue.length > this.maxQueueSize) {
      const removed = queue.splice(0, queue.length - this.maxQueueSize);
      console.log('Clip view queue reached its size limit', {userid: event.userid, teamid: event.teamid, removed: removed.length});
    }
    this.writeQueue(event.userid, event.teamid, queue);
    if (flushImmediately || queue.length >= this.maxBatchSize) this.flush(event.userid, event.teamid);
    else this.scheduleBatchFlush(event.userid, event.teamid);
  }

  flushCurrentUser() {
    const user = this.localStorageService.getUser();
    if (!user?._id) return;
    const teamids = _.compact(_.uniq(_.map(user.userRoles || [], 'teamid')));
    _.forEach(teamids, teamid => this.flush(user._id, teamid));
  }

  async flush(userid: string, teamid: string): Promise<void> {
    if (!userid || !teamid || !navigator.onLine) return;
    const key = `${userid}:${teamid}`;
    if (this.flushing.has(key)) return;
    this.clearBatchFlush(key);
    const user = this.localStorageService.getUser();
    const authorized = user?._id === userid && _.some(user.userRoles || [], {teamid});
    if (!authorized) return;

    const queue = this.readQueue(userid, teamid);
    if (!queue.length) return;
    const batch = queue.slice(0, this.maxBatchSize);
    this.flushing.add(key);
    try {
      const response = await this.clipviewsService.postClipViews(_.map(batch, 'event'));
      if (!response.success) throw new Error('Clip view request was not successful');
      const accepted = response.result?.acceptedEventids || _.map(batch, item => item.event.eventid);
      const rejected = _.compact(_.map(response.result?.rejected || [], rejection => rejection.eventid));
      if (rejected.length) console.log('Clip view events rejected', response.result.rejected);
      const completed = new Set([...accepted, ...rejected]);
      if (!completed.size) throw new Error('Clip view response did not acknowledge any events');
      const remaining = this.readQueue(userid, teamid).filter(item => !completed.has(item.event.eventid));
      this.writeQueue(userid, teamid, remaining);
      this.retryAttempts.delete(key);
      this.clearRetry(key);
      if (remaining.length >= this.maxBatchSize) setTimeout(() => this.flush(userid, teamid), 1000);
      else if (remaining.length) this.scheduleBatchFlush(userid, teamid);
    }
    catch (err) {
      console.log('Unable to submit queued clip views', err);
      this.scheduleRetry(userid, teamid);
    }
    finally {
      this.flushing.delete(key);
    }
  }

  flushWithKeepalive(userid: string, teamid: string) {
    const queue = this.readQueue(userid, teamid).slice(0, this.maxBatchSize);
    if (!queue.length || typeof fetch !== 'function') return;
    const token = this.localStorageService.getToken();
    fetch(`${this.apiHelper.apiUrl()}/view-stats`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', ...(token ? {Authorization: token} : {})},
      body: JSON.stringify({clipviews: _.map(queue, 'event')}),
      keepalive: true,
    }).catch(() => undefined);
  }

  private readQueue(userid: string, teamid: string): QueuedClipView[] {
    const cutoff = Date.now() - this.maxQueueAgeMs;
    const queue = this.localStorageService.getClipViewQueue<QueuedClipView>(userid, teamid);
    const valid = _.filter(queue, item => item?.event?.eventid && new Date(item.queuedAt).getTime() >= cutoff);
    if (valid.length !== queue.length) {
      console.log('Expired queued clip views removed', {userid, teamid, removed: queue.length - valid.length});
      this.writeQueue(userid, teamid, valid);
    }
    return valid;
  }

  private writeQueue(userid: string, teamid: string, queue: QueuedClipView[]) {
    if (queue.length) this.localStorageService.setClipViewQueue(userid, teamid, queue);
    else this.localStorageService.removeClipViewQueue(userid, teamid);
  }

  private scheduleRetry(userid: string, teamid: string) {
    const key = `${userid}:${teamid}`;
    if (this.retryTimers.has(key)) return;
    const attempt = (this.retryAttempts.get(key) || 0) + 1;
    this.retryAttempts.set(key, attempt);
    const delay = Math.min(30000 * Math.pow(2, attempt - 1), this.maxRetryMs);
    this.retryTimers.set(key, setTimeout(() => {
      this.retryTimers.delete(key);
      this.flush(userid, teamid);
    }, delay));
  }

  private scheduleBatchFlush(userid: string, teamid: string) {
    const key = `${userid}:${teamid}`;
    if (this.batchFlushTimers.has(key)) return;
    this.batchFlushTimers.set(key, setTimeout(() => {
      this.batchFlushTimers.delete(key);
      this.flush(userid, teamid);
    }, this.batchFlushIntervalMs));
  }

  private clearBatchFlush(key: string) {
    const timer = this.batchFlushTimers.get(key);
    if (timer) clearTimeout(timer);
    this.batchFlushTimers.delete(key);
  }

  private clearRetry(key: string) {
    const timer = this.retryTimers.get(key);
    if (timer) clearTimeout(timer);
    this.retryTimers.delete(key);
  }
}
